import * as i0 from '@angular/core';
import { PLATFORM_ID, Injectable, Inject, NgModule } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subject } from 'rxjs';

class CoolStorageBase {
    constructor(storageObject, storageObjectName) {
        if (!storageObject) {
            throw new Error(`Current browser does not support ${storageObjectName}`);
        }
        this._storageObject = storageObject;
        this._itemSetSubject = new Subject();
        this._itemRemovedSubject = new Subject();
    }
    get itemSetObservable() {
        return this._itemSetSubject.asObservable();
    }
    get itemRemovedObservable() {
        return this._itemRemovedSubject.asObservable();
    }
    getItem(key) {
        return this._storageObject.getItem(key) || null;
    }
    setItem(key, value) {
        this._itemSetSubject.next({
            key,
            value,
        });
        this._setItemInStorage(key, value);
    }
    removeItem(key) {
        let currentValue = this.tryGetObject(key);
        if (!currentValue) {
            currentValue = this.getItem(key);
        }
        this._itemRemovedSubject.next({
            key,
            value: currentValue,
        });
        this._storageObject.removeItem(key);
    }
    key(index) {
        return this._storageObject.key(index) || null;
    }
    clear() {
        this._storageObject.clear();
    }
    get length() {
        return this._storageObject.length;
    }
    getObject(key) {
        const jsonInStorage = this.getItem(key);
        if (jsonInStorage === null) {
            return null;
        }
        return JSON.parse(jsonInStorage);
    }
    tryGetObject(key) {
        try {
            return this.getObject(key);
        }
        catch (e) {
            return null;
        }
    }
    setObject(key, value) {
        this._itemSetSubject.next({
            key,
            value,
        });
        this._setItemInStorage(key, JSON.stringify(value));
    }
    _setItemInStorage(key, value) {
        this._storageObject.setItem(key, value);
    }
}

class CoolServerStorage {
    constructor() {
        this._storageObject = {};
    }
    getItem(key) {
        return this._storageObject[key] || null;
    }
    setItem(key, value) {
        this._storageObject[key] = value;
    }
    removeItem(key) {
        this._storageObject[key] = undefined;
    }
    key(index) {
        return this._storageObject.key(index) || null;
    }
    clear() {
        this._storageObject = {};
    }
    get length() {
        return Object.keys(this._storageObject).length;
    }
}

class CoolLocalStorage extends CoolStorageBase {
    constructor(platformId) {
        if (isPlatformBrowser(platformId)) {
            super(window.localStorage, 'LocalStorage');
        }
        else {
            super(new CoolServerStorage(), 'LocalStorage');
        }
    }
}
CoolLocalStorage.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: CoolLocalStorage, deps: [{ token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable });
CoolLocalStorage.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: CoolLocalStorage });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: CoolLocalStorage, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [PLATFORM_ID]
                    }] }];
    } });

class CoolSessionStorage extends CoolStorageBase {
    constructor(platformId) {
        if (isPlatformBrowser(platformId)) {
            super(window.sessionStorage, 'SessionStorage');
        }
        else {
            super(new CoolServerStorage(), 'SessionStorage');
        }
    }
}
CoolSessionStorage.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: CoolSessionStorage, deps: [{ token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable });
CoolSessionStorage.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: CoolSessionStorage });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: CoolSessionStorage, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [PLATFORM_ID]
                    }] }];
    } });

class CoolStorageModule {
    static forRoot() {
        return {
            ngModule: CoolStorageModule,
            providers: [
                CoolLocalStorage,
                CoolSessionStorage,
            ]
        };
    }
}
CoolStorageModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: CoolStorageModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CoolStorageModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: CoolStorageModule });
CoolStorageModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: CoolStorageModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: CoolStorageModule, decorators: [{
            type: NgModule,
            args: [{}]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CoolLocalStorage, CoolSessionStorage, CoolStorageModule };
